import axios from 'axios';
import { REQUEST_TIMEOUT_MS } from './csrf';
import type { ChatFeed, ChatReturn } from './conciergeChatTypes';
import { isVideoFile } from './conciergeVideo';
// The CSRF interceptors live in api.ts; importing it guarantees they are installed for these posts.
import './api';

/** The BFF's own upload ladder ends at 50 s; give it the whole of that and a little more. */
const FILE_TIMEOUT_MS = 55000;
/**
 * A video has no total limit — a slow phone may take long over 100 MB — only a stall: no progress
 * for a minute. After its last byte the BFF still uploads it to Verbox (≤ 150 s) before answering.
 */
const VIDEO_STALL_MS = 60000;
const VIDEO_ANSWER_MS = 200000;

export const fetchFeed = async (uuid: string): Promise<ChatFeed> => {
  const res = await axios.post('/api/concierge/feed', { uuid }, { timeout: REQUEST_TIMEOUT_MS });
  return res.data.data as ChatFeed;
};

export const sendMessage = async (uuid: string, text: string): Promise<void> => {
  await axios.post('/api/concierge/message', { uuid, text: text.trim() }, { timeout: REQUEST_TIMEOUT_MS });
};

/**
 * The file must already be the materialised copy from `prepareReceipt`; `onProgress` gets 0..1.
 * `uploadId` names this attempt of a video, so a `file_failed` signal can point back at it.
 */
export const sendFile = async (uuid: string, file: File, onProgress?: (share: number) => void, uploadId?: string): Promise<void> => {
  const body = new FormData();
  body.append('uuid', uuid);
  body.append('file', file, file.name);
  if (uploadId) body.append('uploadId', uploadId);

  const video = isVideoFile(file);
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = (ms: number) => {
    clearTimeout(timer);
    timer = setTimeout(() => controller.abort(), ms);
  };
  if (video) arm(VIDEO_STALL_MS);

  try {
    await axios.post('/api/concierge/file', body, {
      timeout: video ? 0 : FILE_TIMEOUT_MS,
      signal: controller.signal,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.min(1, e.loaded / e.total));
        if (video) arm(e.total && e.loaded >= e.total ? VIDEO_ANSWER_MS : VIDEO_STALL_MS);
      },
    });
  } finally {
    clearTimeout(timer);
  }
};

export const returnToMerchant = async (uuid: string): Promise<ChatReturn> => {
  const res = await axios.post('/api/concierge/return', { uuid }, { timeout: REQUEST_TIMEOUT_MS });
  return res.data.data as ChatReturn;
};
